import { useState, useEffect } from "react";
import { getDownloadURL, listAll, ref, deleteObject } from "firebase/storage";
import { collection, query, orderBy, getDocs, deleteDoc, doc, where } from "firebase/firestore";
import { storage, auth, functions, db } from "../firebase";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Dialog } from "@headlessui/react";
import { httpsCallable } from "firebase/functions";
import { Eye, Trash2, Download, Wand2, Sparkles, MessageSquareShare, Grid, List, XCircle, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
function Outputs() {
  // Core states
  const [generatedImages, setGeneratedImages] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("images");
  const [videoList, setVideoList] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoError, setVideoError] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoCurrentPage, setVideoCurrentPage] = useState(1);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoPreviewModal, setShowVideoPreviewModal] = useState(false);


  // UI states
  const [viewMode, setViewMode] = useState(localStorage.getItem('viewMode') || 'grid');
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 8;

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDesignRequestModal, setShowDesignRequestModal] = useState(false);

  // Selection states
  const [selectedImage, setSelectedImage] = useState(null);
  const [enhancementMessage, setEnhancementMessage] = useState("");
  const [designRequestMessage, setDesignRequestMessage] = useState("");
  const [shareOptions, setShareOptions] = useState({
    copyLink: true,
    download: true,
    socialMedia: true
  });

  // Calculate pagination
  const pageCount = Math.ceil(generatedImages.length / imagesPerPage);
  const paginatedImages = generatedImages.slice(
    (currentPage - 1) * imagesPerPage,
    currentPage * imagesPerPage
  );
  const videosPerPage = 8;
  const videoPageCount = Math.ceil(videoList.length / videosPerPage);
  const paginatedVideos = videoList.slice(
    (videoCurrentPage - 1) * videosPerPage,
    videoCurrentPage * videosPerPage
  );
  const handleVideoPageChange = (pageNumber) => {
    setVideoCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };


  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem('viewMode', viewMode);
    localStorage.setItem('currentPage', currentPage.toString());
  }, [viewMode, currentPage]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        fetchGeneratedImages(user.uid);
      } else {
        setUserId(null);
        setGeneratedImages([]);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
  if (activeTab === "videos" && userId) {
      fetchGeneratedVideos(userId);
  }
  }, [activeTab, userId]);

  const handleDeleteVideo = async (video) => {
  try {
    // Delete from Firebase Storage
    await deleteObject(video.storageRef);
    
    // Delete from Firestore (if using new structure)
    if (video.id && !video.id.includes('/')) { // Check if it's a document ID (not a path)
      const videoDocRef = doc(db, "users", userId, "videos", video.id);
      await deleteDoc(videoDocRef);
    }
    
    setVideoList(prev => prev.filter(v => v.id !== video.id));
    toast.success("Video deleted successfully");
  } catch (err) {
    console.error("Error deleting video:", err);
    toast.error("Failed to delete video");
  }
};


 const fetchGeneratedVideos = async (uid) => {
  if (!uid) return;

  setVideoLoading(true);
  setVideoError(null);

  try {
    // 🔹 Step 1: Query Firestore for user's videos (ordered newest first)
    const videosRef = collection(db, "users", uid, "videos");
    let querySnapshot;

    try {
      const q = query(videosRef, orderBy("created_at", "desc"));
      querySnapshot = await getDocs(q);
    } catch (orderByError) {
      console.warn("OrderBy failed, falling back:", orderByError);
      querySnapshot = await getDocs(videosRef);
    }

    // 🔹 Step 2: If no docs in Firestore, fallback to direct Storage listAll
    if (querySnapshot.empty) {
      console.log("No Firestore docs, falling back to Storage listAll...");
      const folderRef = ref(storage, `assets/${uid}/videos`);
      const result = await listAll(folderRef);

      if (result.items.length === 0) {
        setVideoList([]);
        setVideoError("No videos found");
        return;
      }

      const files = await Promise.all(
        result.items.map(async (item) => ({
          id: item.fullPath,
          url: await getDownloadURL(item),
          filename: item.name,
          storageRef: item
        }))
      );

      // Sort newest first by filename (timestamp is in name)
      files.sort((a, b) => b.filename.localeCompare(a.filename));

      setVideoList(files);
      return;
    }

    // 🔹 Step 3: Build video list from Firestore docs
    const files = [];
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data();

      // Always resolve fresh URL from storage_path
      let videoUrl = null;
      try {
        if (data.storage_path) {
          const storageRef = ref(storage, data.storage_path);
          videoUrl = await getDownloadURL(storageRef);
        }
      } catch (urlErr) {
        console.error("Failed to resolve video URL for:", data, urlErr);
      }

      files.push({
        id: docSnapshot.id,
        url: videoUrl,
        prompt: data.prompt || "",
        engine: data.engine || "",
        created_at: data.created_at || null,
        filename: data.filename || "",
        storage_path: data.storage_path || null,
      });
    }

    // 🔹 Step 4: Ensure sorted by created_at
    if (files.length > 1) {
      files.sort((a, b) => {
        const timeA = a.created_at?.toDate
          ? a.created_at.toDate().getTime()
          : new Date(a.created_at).getTime();
        const timeB = b.created_at?.toDate
          ? b.created_at.toDate().getTime()
          : new Date(b.created_at).getTime();
        return timeB - timeA; // newest first
      });
    }

    setVideoList(files);
  } catch (err) {
    console.error("❌ Error fetching videos:", err);
    setVideoError("Failed to fetch videos");
  } finally {
    setVideoLoading(false);
  }
};

  const fetchGeneratedImages = async (uid) => {
    if (!uid) return;
    setLoading(true);
    console.log("Fetching images for UID:", uid);
    
    try {
      const outputRef = ref(storage, `assets/${uid}/outputs`);
      const res = await listAll(outputRef);
      console.log("Items found in storage:", res.items.length);

      if (res.items.length === 0) {
        setGeneratedImages([]);
        return;
      }

      // Fetch Firestore asset documents for better timestamp accuracy
      const assetDocsSnap = await getDocs(collection(db, "users", uid, "assets"));
      const assetDocs = {};
      assetDocsSnap.forEach(docSnap => {
        assetDocs[docSnap.id] = docSnap.data();
      });

      const urls = await Promise.all(
        res.items.map(async (item) => {
          try {
            // Get download URL with custom token if needed
            const url = await getDownloadURL(item);
            
            // Create a new URL object and modify it to use direct storage access
            const modifiedUrl = new URL(url);
            modifiedUrl.searchParams.append('alt', 'media');
            
            // Extract name without extension
            const imageNameWithoutExt = item.name.split(".")[0];
            
            // Get Firestore metadata if exists
            const firestoreData = assetDocs[imageNameWithoutExt] || {};
            
            // Use Firestore timestamp if available, otherwise fallback to filename timestamp
            let timestamp = 0;
            if (firestoreData.timestamp) {
              timestamp = firestoreData.timestamp;
            } else if (firestoreData.timeCreated) {
              timestamp = firestoreData.timeCreated;
            } else {
              // Fallback: extract timestamp from filename (for generated images like output-1745221870265)
              const match = item.name.match(/output-(\d+)/);
              timestamp = match ? Number(match[1]) : item.timeCreated || Date.now();
            }
            
            console.log("Processing image:", {
              name: item.name,
              path: item.fullPath,
              timestamp: timestamp,
              firestoreTimestamp: firestoreData.timestamp
            });

            return {
              url: modifiedUrl.toString(),
              path: item.fullPath,
              name: item.name,
              timestamp: timestamp
            };
          } catch (error) {
            console.error("Error processing image:", {
              name: item.name,
              error: error.message
            });
            return null;
          }
        })
      );

      const validUrls = urls.filter(url => url !== null);
      console.log(`Successfully processed ${validUrls.length} out of ${res.items.length} images`);

      if (validUrls.length === 0) {
        toast.error("No images could be loaded");
      } else {
        // Sort by timestamp (newest first)
        validUrls.sort((a, b) => b.timestamp - a.timestamp);
        setGeneratedImages(validUrls);
      }
    } catch (error) {
      console.error("Storage error:", error);
      toast.error(error.message || "Failed to fetch images");
    } finally {
      setLoading(false);
    }
  };

  // Image component with better error handling
  const ImageWithFallback = ({ src, alt, className, ...props }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
      setImgSrc(src);
      setLoading(true);
      setError(false);
    }, [src]);

    return (
      <>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#3913C5] border-t-transparent"></div>
          </div>
        )}
        <img
          src={imgSrc}
          alt={alt}
          className={className}
          onLoad={() => setLoading(false)}
          onError={() => {
            setError(true);
            setLoading(false);
            setImgSrc('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23eee"/><text x="50%" y="50%" font-family="Arial" font-size="14" fill="%23999" text-anchor="middle" dy=".3em">Image Error</text></svg>');
          }}
          {...props}
        />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500 text-sm">
            Failed to load image
          </div>
        )}
      </>
    );
  };

  const handleDelete = async (imagePath) => {
    setSelectedImage(generatedImages.find(img => img.path === imagePath));
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedImage) return;
    try {
      await deleteObject(ref(storage, selectedImage.path));
      setGeneratedImages(generatedImages.filter((img) => img.path !== selectedImage.path));
      toast.success("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    } finally {
      setShowDeleteConfirm(false);
      setSelectedImage(null);
    }
  };

 const handleDownload = async (url, index) => {
  try {
    const response = await fetch("https://download.styleuai.com/download-image", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        image_url: url,
        filename: `StyleUAI_${index + 1}.jpg`,
      }),
    });

    if (!response.ok) throw new Error("Failed to download image");

    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `StyleUAI_${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Failed to download image");
  }
};


  const handleShare = async (url) => {
    setSelectedImage(generatedImages.find(img => img.url === url));
    setShowShareModal(true);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied to clipboard");
    } catch (error) {
      console.error("Copy error:", error);
      toast.error("Failed to copy link");
    }
  };

  const shareOnSocialMedia = (platform) => {
    const shareUrl = selectedImage?.url;
    if (!shareUrl) return;

    const platforms = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    };

    if (platforms[platform]) {
      window.open(platforms[platform], '_blank');
    }
  };

  const handleEnhancementRequest = async () => {
    if (!enhancementMessage) {
      toast.error("Please enter enhancement details");
      return;
    }
    const sendEnhancementRequest = httpsCallable(functions, "sendEnhancementRequest");
    try {
      await sendEnhancementRequest({
        userId,
        imageUrl: selectedImage.url,
        message: enhancementMessage,
      });
      toast.success("Enhancement request sent! Please wait 24-48 hours.");
    } catch (error) {
      console.error("Error sending enhancement request:", error);
      toast.error("Failed to send request");
    }
    setEnhancementMessage("");
    setShowEnhanceModal(false);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('viewMode', mode);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleDesignRequest = async () => {
    if (!designRequestMessage.trim()) {
      toast.error("Please enter your design request details");
      return;
    }

    try {
      const sendDesignRequest = httpsCallable(functions, "sendDesignRequest");
      await sendDesignRequest({
        userId,
        message: designRequestMessage,
      });
      toast.success("Design request submitted successfully!");
      setShowDesignRequestModal(false);
      setDesignRequestMessage("");
    } catch (error) {
      console.error("Error submitting design request:", error);
      toast.error("Failed to submit design request");
    }
  };

  return (
    <div className="flex h-screen">
      <div className="hidden md:block">
    <Sidebar />
  </div>
      <div className="flex flex-col flex-1 bg-white overflow-x-hidden">
      <div className="absolute top-2 md:top-4 right-2 md:right-4">
    <Header />
  </div>
 <div className="block md:hidden">
      <Sidebar />
    </div>

<div className="content-container">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 md:mb-6">
              <div className="mb-2 sm:mb-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Generated Outputs</h1>
                <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-1 mb-2">View and manage all your generated images.</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setActiveTab("images")}
                    className={`px-2 py-2 rounded-lg font-medium text-sm ${
                      activeTab === "images"
                        ? "bg-[#3913C5] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Images
                  </button>
                  <button
                    onClick={() => setActiveTab("videos")}
                    className={`px-4 py-2 rounded-lg font-medium text-sm ${
                      activeTab === "videos"
                        ? "bg-[#3913C5] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Videos
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => handleViewModeChange("grid")}
                  className={`p-1 sm:p-1.5 md:p-2 rounded-md ${
                    viewMode === "grid" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <Grid className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange("list")}
                  className={`p-1 sm:p-1.5 md:p-2 rounded-md ${
                    viewMode === "list" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <List className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
                </button>
              </div>
            </div>

           {activeTab === "images" && (
  loading ? (
    <div className="flex justify-center items-center h-48">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3913C5]"></div>
    </div>
  ) : generatedImages.length > 0 ? (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {paginatedImages.map((img, index) => (
          <div 
            key={img.path} 
            className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
          >
            <ImageWithFallback
              src={img.url}
              alt={`Generated ${index + 1}`}
              className="absolute inset-0 w-full h-full object-contain z-10"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100 z-20">
              <div className="flex gap-2">
                <button onClick={() => { setSelectedImage(img); setShowPreviewModal(true); }} className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors" title="Preview">
                  <Eye className="w-5 h-5 text-gray-700" />
                </button>
                <button onClick={() => handleDownload(img.url, index)} className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors" title="Download">
                  <Download className="w-5 h-5 text-gray-700" />
                </button>
                <button onClick={() => handleShare(img.url)} className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors" title="Share">
                  <Share2 className="w-5 h-5 text-gray-700" />
                </button>
                <button onClick={() => { setSelectedImage(img); setShowEnhanceModal(true); }} className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors" title="Enhancement Request">
                  <Wand2 className="w-5 h-5 text-gray-700" />
                </button>
                <button onClick={() => handleDelete(img.path)} className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors" title="Delete">
                  <Trash2 className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination for Images */}
      {pageCount > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-4 py-2 bg-[#3913C5] text-white rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] disabled:hover:bg-[#3913C5] flex items-center gap-2">
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <div className="flex gap-2">
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((number) => (
              <button key={number} onClick={() => handlePageChange(number)} className={`px-4 py-2 rounded-lg transition-colors ${currentPage === number ? 'bg-[#3913C5] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {number}
              </button>
            ))}
          </div>
          <button onClick={() => handlePageChange(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="px-4 py-2 bg-[#3913C5] text-white rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] disabled:hover:bg-[#3913C5] flex items-center gap-2">
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  ) : (
    <div className="text-center py-12">
      <p className="text-gray-500">No images found</p>
    </div>
  )
)}

{activeTab === "videos" && (
  videoLoading ? (
    <div className="flex justify-center items-center h-48">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3913C5]"></div>
    </div>
  ) : videoError ? (
    <div className="text-center py-12 text-red-500">
      <p>{videoError}</p>
    </div>
  ) : paginatedVideos.length > 0 ? (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {paginatedVideos.map((vid) => (
        <div
        key={vid.id}
        className="group relative aspect-square rounded-xl overflow-hidden shadow-md"
        style={{ backgroundColor: "#f7f7f7" }}
      >
        {/* 1️⃣ autoplay thumbnail */}
        <video
          src={vid.url}
          muted
          loop
          playsInline
          autoPlay
          className="absolute inset-0 w-full h-full object-contain bg-black"
        />

        {/* 2️⃣ dark overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors" />

        {/* 3️⃣ PREVIEW button */}
        <button
          onClick={() => {
            setSelectedVideo(vid.url);
            setShowVideoPreviewModal(true);
          }}
          title="Preview"
          className="invisible group-hover:visible absolute left-3 bottom-3 z-20 p-2 bg-white rounded-full shadow"
        >
          <Eye className="w-5 h-5 text-gray-700" />
        </button>

        {/* 4️⃣ DELETE button */}
        <button
          onClick={() => handleDeleteVideo(vid)}
          title="Delete"
          className="invisible group-hover:visible absolute right-3 bottom-3 z-20 p-2 bg-white rounded-full shadow"
        >
          <Trash2 className="w-5 h-5 text-gray-700" />
        </button>
      </div>
    ))}


      </div>

      {/* Pagination for Videos */}
      {videoPageCount > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => handleVideoPageChange(Math.max(1, videoCurrentPage - 1))}
            disabled={videoCurrentPage === 1}
            className="px-4 py-2 bg-[#3913C5] text-white rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] disabled:hover:bg-[#3913C5] flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <div className="flex gap-2">
            {Array.from({ length: videoPageCount }, (_, i) => i + 1).map((number) => (
              <button
                key={number}
                onClick={() => handleVideoPageChange(number)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  videoCurrentPage === number
                    ? "bg-[#3913C5] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {number}
              </button>
            ))}
          </div>
          <button
            onClick={() => handleVideoPageChange(Math.min(videoPageCount, videoCurrentPage + 1))}
            disabled={videoCurrentPage === videoPageCount}
            className="px-4 py-2 bg-[#3913C5] text-white rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] disabled:hover:bg-[#3913C5] flex items-center gap-2"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  ) : (
    <div className="text-center py-12">
      <p className="text-gray-500">No videos found</p>
    </div>
  )
)}
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog
          open={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Image Preview</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            {selectedImage && (
              <ImageWithFallback
                src={selectedImage.url}
                alt="Preview"
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
            )}
          </div>
        </Dialog>

        {/* Video Preview Modal */}
        <Dialog
          open={showVideoPreviewModal}
          onClose={() => setShowVideoPreviewModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Video Preview</h3>
              <button
                onClick={() => setShowVideoPreviewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            {selectedVideo && (
              <video
                src={selectedVideo}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                controls
                autoPlay
              />
            )}
          </div>
        </Dialog>


        {/* Enhancement Request Modal */}
        <Dialog
          open={showEnhanceModal}
          onClose={() => setShowEnhanceModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="relative bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Enhancement Request</h3>
              <button
                onClick={() => setShowEnhanceModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-4 mb-4"
              rows={5}
              placeholder="Describe the enhancements you need..."
              value={enhancementMessage}
              onChange={(e) => setEnhancementMessage(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEnhanceModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEnhancementRequest}
                className="px-4 py-2 bg-[#3913C5] text-white rounded-lg hover:bg-[#2a0d8f]"
              >
                Submit Request
              </button>
            </div>
          </div>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="relative bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Image</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this image? This action cannot be undone.</p>
              <div className="flex gap-4 w-full">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Dialog>

        {/* Share Modal */}
        <Dialog
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="relative bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Share Image</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => copyToClipboard(selectedImage?.url)}
                className="w-full py-2 px-4 bg-[#3913C5] text-white rounded-lg hover:bg-[#2a0d8f] flex items-center justify-center gap-2"
              >
                <Share2 className="w-5 h-5" />
                Copy Link
              </button>
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-600 mb-3">Share on Social Media</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => shareOnSocialMedia('twitter')}
                    className="p-2 bg-[#1DA1F2] text-white rounded-lg hover:bg-opacity-90"
                  >
                    Twitter
                  </button>
                  <button
                    onClick={() => shareOnSocialMedia('facebook')}
                    className="p-2 bg-[#1877F2] text-white rounded-lg hover:bg-opacity-90"
                  >
                    Facebook
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Dialog>

        {/* Design Request Modal */}
        <Dialog
          open={showDesignRequestModal}
          onClose={() => setShowDesignRequestModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Request New Design</h3>
              <button
                onClick={() => setShowDesignRequestModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <textarea
              className="w-full border border-gray-300 p-4 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#3913C5]"
              placeholder="Describe the design you want..."
              value={designRequestMessage}
              onChange={(e) => setDesignRequestMessage(e.target.value)}
              rows={5}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowDesignRequestModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDesignRequest}
                className="px-4 py-2 bg-[#3913C5] text-white rounded-lg hover:bg-[#2a0d8f] transition-colors"
              >
                Submit Request
              </button>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
    </div>
  );
}

export default Outputs;
